import { LoadingButton } from '@mui/lab';
import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  // styled,
  TextField,
  Stack,
  Typography,
  Paper,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  SvgIcon,
  Button,
  DialogActions,
  DialogTitle,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import { RiDoubleQuotesL } from 'react-icons/ri';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Transition } from '../transition';
import { useFormik } from 'formik';
import { ApiEndpoint, AppColors, config, typographyClamps } from '../../utils/utils';
import { bookDemoFormSchema } from '../../validation/bookDemoFormSchema';
import { getAssetURL, getCapitalizedString } from '../../utils/utilMethods';
import { axiosInstance } from '../../client';
import VideoModel from './VideoModel';
import { useEffect, useState } from 'react';
import useToggle from '../../hooks/useToggle';
import { getItem } from '../../utils/localStorage';
import { IsNotNullOrEmpty } from '../../utils/valid';

// Types
type DialogStep = 'dealer' | 'confirmation';

interface Feature {
  iconPath?: string;
  title: string;
}

const featureList: Feature[] = [
  {
    title: 'Capture Every Buyer Instantly',
  },
  {
    title: "Control the Buyer's First Step",
  },
  {
    title: 'Route Leads to the Right Rep in Real Time',
  },
];

const ROOFTOP_OPTIONS = ['1', '2-5', '6-15', '15+'];

interface IBookDemoFormState {
  firstName: string;
  lastName: string;
  dealershipName: string;
  email: string;
  phone?: string;
  rooftops?: string;
  referralCode?: string;
}

interface IProps {
  toggle: boolean;
  onClose: () => void;
  onSubmitSuccess?: (code: string) => void;
}

interface ISalesRepResponse {
  firstName?: string;
  lastName?: string;
  userProfileImage?: {
    id?: string;
    key?: string;
    fileName?: string;
    url?: string;
  };
}

const BookDemoFormModel: React.FC<IProps> = ({
  toggle,
  onClose,
  onSubmitSuccess,
}) => {
  const [step, setStep] = useState<DialogStep>('dealer');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [videoUrl, setVideoUrl] = useState<string | undefined>();
  // const [isPlayIntroVideo] = useState<boolean>(false);

  const [referralCode, setReferralCode] = useState<string | undefined>();
  const [salesRep, setSalesRep] = useState<ISalesRepResponse | undefined>();
  const [isSalesRepLoading, setIsSalesRepLoading] = useState<boolean>(false);

  const {
    toggle: toggleVideo,
    onOpen: onOpenVideo,
    onClose: onCloseVideo,
  } = useToggle();

  const inputFieldLabelStyle = {
    color: AppColors.blackColor,
    fontWeight: 600,

    mb: 0.5,
    '& span': {
      color: '#B91C1C',
    },
  };

  const textFieldStyle = {
    '& label': {
      color: AppColors.grayThreeColor,
    },
    '& .MuiOutlinedInput-root input': {
      color: AppColors.blackColor,
      paddingTop: '10px', // Reduced top padding
      paddingBottom: '10px', // Reduced bottom padding
    },
    // For Select component - reduce padding
    '& .MuiSelect-select': {
      paddingTop: '10px', // Reduced top padding for Select
      paddingBottom: '10px', // Reduced bottom padding for Select
    },
    '& .MuiOutlinedInput-root': {
      // borderRadius: '8px', // Added border radius
      // background: AppColors.grayZeroColor,
      '& fieldset': {
        borderColor: AppColors.grayOneColor,
        // borderRadius: '8px', // Added border radius to fieldset
      },
      '&:hover fieldset': {
        borderColor: AppColors.grayOneColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: AppColors.grayOneColor,
      },
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: ` ${AppColors.grayOneColor} !important`,
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: `${AppColors.primaryColor} !important`,
    },
    '& .MuiInputLabel-shrink': {
      backgroundColor: AppColors.grayZeroColor,
      paddingLeft: '5px',
      paddingRight: '5px',
      borderRadius: '5px',
    },
  };

  const getBorderBottom = { borderBottom: `1px solid #e0e0e0` };

  const ThickCheckIcon = (props: any) => {
    return (
      <SvgIcon {...props} viewBox='0 0 24 24'>
        <path
          d='M5 13l4 4L19 7'
          fill='none'
          stroke='currentColor'
          strokeWidth='4'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </SvgIcon>
    );
  };


  useEffect(() => {
    if (!toggle) {
      return;
    }

    const storedReferralCode = getItem(config.REFERRAL_CODE_PARAM_KEY);
    if (IsNotNullOrEmpty(storedReferralCode)) {
      setReferralCode(storedReferralCode);
      return;
    }

    setReferralCode(undefined);
  }, [toggle]);

  useEffect(() => {
    const getSalesRepByReferralCode = async () => {
      if (!IsNotNullOrEmpty(referralCode)) {
        setSalesRep(undefined);
        return;
      }

      try {
        setIsSalesRepLoading(true);
        const response = await axiosInstance.get(
          `${ApiEndpoint.SALES_REP_LIST}/${encodeURIComponent(referralCode as string)}`,
        );
        setSalesRep(response?.data);
      } catch (error) {
        setSalesRep(undefined);
        console.error(error);
      } finally {
        setIsSalesRepLoading(false);
      }
    };

    getSalesRepByReferralCode();
  }, [referralCode]);

  const salesRepName = `${salesRep?.firstName ?? ''} ${salesRep?.lastName ?? ''}`.trim();


  const {
    handleSubmit,
    values,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
  } = useFormik<IBookDemoFormState>({
    initialValues: {
      firstName: '',
      lastName: '',
      dealershipName: '',
      email: '',
      phone: '',
      rooftops: '1',
    },
    validationSchema: bookDemoFormSchema,
    onSubmit: async (values) => {
      try {
        const reqBody = {
          ...{ ...values, leadType: 'dealer' },
        };


        if (IsNotNullOrEmpty(referralCode)) {
          reqBody.referralCode = referralCode;
        }

        const response = await axiosInstance.post(ApiEndpoint.REQUEST_DEMO, reqBody);

        // showSuccessMessage(
        //   `Thanks for your interest. An agent will be in touch with you shortly.`,
        // );

        if (response && response.data && response.data.id) {
          onSubmitSuccess && onSubmitSuccess(response.data.id);
          setStep('confirmation');
        }
      } catch (error: any) {
        console.error(error);
      }
    },
  });

  useEffect(() => {
    if (toggleVideo === false) {
      setVideoUrl(undefined);
    }
  }, [toggleVideo]);

  const handlePlayVideo = (videoUrl: string | undefined) => {
    if (videoUrl !== undefined) {
      setVideoUrl(videoUrl);
      onOpenVideo();
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setStep('dealer');
      resetForm();
    }, 200);
  };

  const renderConfirmation = () => (
    <>
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          display='flex'
          alignItems='center'
          textAlign={'center'}
          sx={{ ...getBorderBottom }}
        >
          <Typography
            variant={isMobile ? 'h6' : 'h5'}
            component='div'
            fontWeight='600'
            sx={{
              flexGrow: 1,
            }}
          >
            Thank You!
          </Typography>
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant='h3' sx={{ mb: 2 }}>
            <VerifiedIcon color='success' sx={{ fontSize: 56 }} />
          </Typography>
          <Typography variant='h6' gutterBottom>
            Your request has been submitted!
          </Typography>
          <Typography variant='body1' color='text.secondary'>
            Thanks for your interest. An agent will be in touch with you
            shortly.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center' }}>
        <Button
          variant='contained'
          onClick={handleClose}
          size='large'
          sx={{ px: 4 }}
        >
          Close
        </Button>
      </DialogActions>
    </>
  );

  const renderDealerStep = () => (
    <>
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
        }}
      >
        <CloseIcon fontSize='small' />
      </IconButton>
      {/* <BannerDialogTitle
        sx={{
          color: AppColors.primaryColor,
          textAlign: { md: 'start', xs: 'center' },
        }}
      >
        Book a Demo
      </BannerDialogTitle> */}
      <form onSubmit={handleSubmit} autoComplete='off'>
        <DialogContent
          sx={{
            paddingX: '24px',
            background: `url('/static/home_page/img_book_demo_background.png')`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
          }}
        >
          <Grid container justifyContent={'center'} gap={3}>
            <Grid size={{ xs: 12, sm: 12, md: 5.8 }} alignContent={'center'}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  color: AppColors.blackColor,
                  gap: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 'clamp(1.25rem, 3vw, 2rem)', // h3 size
                    fontWeight: 700,

                    lineHeight: 1,
                  }}
                >
                  Request 1-on-1
                  <Typography
                    sx={{
                      color: '#1A4479',
                      fontSize: 'inherit',
                      fontWeight: 'inherit',
                    }}
                  >
                    QRBuddy Demo
                  </Typography>
                </Typography>

                <Typography
                  sx={{
                    fontSize: 'clamp(0.875rem, 2vw, 1.25rem)', // h5 size
                  }}
                >
                  See how QRBuddy controls every buyer's first step — whether
                  they scan the lot, visit your website, or call your
                  dealership.
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    handlePlayVideo(
                      'https://assets.qrbuddy.ai/web/Demo-Form-Video.mp4',
                    );
                  }}
                >
                  <Box
                    component='img'
                    src='/static/home_page/book_demo_form_thumb.png'
                    alt='video thumbnail'
                    sx={{ width: { xs: '90%', sm: '100%' } }}
                  // onClick={handleCallToAssistant}
                  />

                  {/* <Box
                    component='img'
                    src='/static/images/ic_play_button.svg'
                    alt='Play'
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      cursor: 'pointer',
                      transform: 'translate(-50%, -50%)',
                      width: { xs: '80px', md: '100px' },
                      height: { xs: '80px', md: '100px' },
                    }}
                    onClick={() => {
                      handlePlayVideo(
                        'https://assets.qrbuddy.ai/web/Demo-Form-Video.mp4',
                      );
                    }}
                  /> */}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    mt: 1,
                  }}
                >
                  <Box
                    sx={{
                      color: '#A5BFE6',
                      fontSize: 32,
                      display: 'flex',
                      flexShrink: 0,
                      mt: -1,
                    }}
                  >
                    {RiDoubleQuotesL({}) as React.ReactElement}
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: 'clamp(0.875rem, 2vw, 1.25rem)', // h5 size
                        color: AppColors.blackColor,
                        mb: 2,
                        '& span': {
                          color: '#1A4479',
                        },
                      }}
                    >
                      QRBuddy finally gave us control over how buyers enter our
                      dealership — on the lot and <strong>online</strong>.&nbsp;
                      <span>”</span>
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#64748B',
                        fontWeight: 400,
                        textAlign: 'end',
                        pr: { xs: 2, sm: 4 },
                      }}
                    >
                      — Dealer Principal / GM
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 5.8 }}>
              <Paper
                sx={{
                  py: 2,
                  px: { xs: 2, sm: 4 },
                  mt: '15%',
                  position: 'relative',
                  pt: '15%', // Add top padding to make room for the image
                }}
              >
                <Box
                  component={'img'}
                  src='/static/images/miles_ai.png'
                  sx={{
                    height: { xs: '12%', sm: '20%', md: '25%' },
                    width: 'auto',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(5deg)', // Center horizontally and move up by half its height
                    // px: 2, // Optional: add horizontal padding around the image
                  }}
                />
                <Stack direction='column' spacing={2}>
                  <Box sx={{ pt: { xs: 2, sm: 1 } }}>
                    <Typography
                      sx={{
                        fontSize: {
                          xs: typographyClamps.h6,
                          sm: typographyClamps.h5,
                        },
                        fontWeight: 600,
                        color: '#0A2954',
                      }}
                    >
                      Schedule Your QRBuddy Demo
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: {
                          xs: typographyClamps.body2,
                          sm: typographyClamps.body1,
                        },
                      }}
                    >
                      Built specifically for dealerships. No obligation. No
                      pressure.
                    </Typography>
                  </Box>
                  <Grid container gap={2} justifyContent={'center'}>
                    <Grid size={{ xs: 12, md: 5.7 }}>
                      <Typography sx={inputFieldLabelStyle}>
                        First Name<span>*</span>
                      </Typography>
                      <TextField
                        id='firstName'
                        name='firstName'
                        fullWidth
                        onChange={handleChange}
                        value={values.firstName}
                        onInput={(e: any) => {
                          e.target.value = getCapitalizedString(
                            e.target.value ?? '',
                          );
                        }}
                        error={Boolean(errors.firstName && touched.firstName)}
                        helperText={
                          Boolean(errors.firstName && touched.firstName) &&
                          errors.firstName
                        }
                        sx={{ ...textFieldStyle }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 5.7 }}>
                      <Typography sx={inputFieldLabelStyle}>
                        Last Name<span>*</span>
                      </Typography>
                      <TextField
                        id='lastName'
                        name='lastName'
                        fullWidth
                        onChange={handleChange}
                        value={values.lastName}
                        onInput={(e: any) => {
                          e.target.value = getCapitalizedString(
                            e.target.value ?? '',
                          );
                        }}
                        error={Boolean(errors.lastName && touched.lastName)}
                        helperText={
                          Boolean(errors.lastName && touched.lastName) &&
                          errors.lastName
                        }
                        sx={{ ...textFieldStyle }}
                      />
                    </Grid>
                  </Grid>

                  <Box>
                    <Typography sx={inputFieldLabelStyle}>
                      Dealership Name
                    </Typography>
                    <TextField
                      id='dealershipName'
                      name='dealershipName'
                      fullWidth
                      onChange={handleChange}
                      value={values.dealershipName}
                      onInput={(e: any) => {
                        e.target.value = getCapitalizedString(
                          e.target.value ?? '',
                        );
                      }}
                      error={Boolean(
                        errors.dealershipName && touched.dealershipName,
                      )}
                      helperText={
                        Boolean(
                          errors.dealershipName && touched.dealershipName,
                        ) && errors.dealershipName
                      }
                      sx={{ ...textFieldStyle }}
                    />
                  </Box>


                  <Grid container gap={2} justifyContent={'center'}>
                    <Grid size={{ xs: 12, md: 5.7 }}>
                      <Typography sx={inputFieldLabelStyle}>
                        Email<span>*</span>
                      </Typography>
                      <TextField
                        type='email'
                        name='email'
                        id='email'
                        fullWidth
                        onChange={handleChange}
                        value={values.email}
                        error={Boolean(errors.email && touched.email)}
                        helperText={
                          Boolean(errors.email && touched.email) && errors.email
                        }
                        sx={{ ...textFieldStyle }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 5.7 }}>
                      <Typography sx={inputFieldLabelStyle}>
                        Mobile Phone<span>*</span>
                      </Typography>
                      <TextField
                        type='tel'
                        name='phone'
                        id='phone'
                        // placeholder='Required to contact you via text message'
                        fullWidth
                        onChange={handleChange}
                        value={values.phone}
                        error={Boolean(errors.phone && touched.phone)}
                        helperText={
                          Boolean(errors.phone && touched.phone) && errors.phone
                        }
                        sx={{ ...textFieldStyle }}
                      />
                    </Grid>
                  </Grid>



                  <Grid container gap={2} justifyContent={'center'}>
                    <Grid size={{ xs: 12, md: referralCode ? 5.7 : 12 }}>
                      <Typography sx={inputFieldLabelStyle}>
                        Dealership Size
                      </Typography>
                      <FormControl
                        fullWidth
                        error={Boolean(errors.rooftops && touched.rooftops)}
                        sx={{ ...textFieldStyle }}
                      >
                        <Select
                          labelId='rooftops'
                          id='rooftops'
                          name='rooftops'
                          value={values.rooftops}
                          onChange={handleChange}
                          MenuProps={{
                            disablePortal: true,
                            PaperProps: {
                              sx: {
                                zIndex: 1400,
                              },
                            },
                          }}
                        >
                          {ROOFTOP_OPTIONS.map((item) => {
                            return (
                              <MenuItem value={item}>
                                <Box>
                                  {item} {item === '1' ? 'Rooftop' : 'Rooftops'}
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </Select>

                        {Boolean(errors.rooftops && touched.rooftops) && (
                          <FormHelperText>{errors.rooftops}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                    {referralCode && (salesRep || isSalesRepLoading) && (
                      <Grid size={{ xs: 12, md: 5.7 }}>
                        <Typography sx={inputFieldLabelStyle}>
                          Sales Rep:
                        </Typography>
                        <Box
                          sx={{
                            minHeight: 40,
                            border: `1px solid ${AppColors.grayOneColor}`,
                            borderRadius: 1,
                            px: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          {isSalesRepLoading ? (
                            <>
                              <CircularProgress size={18} />
                              <Typography sx={{ color: AppColors.grayThreeColor }}>
                                Loading sales rep...
                              </Typography>
                            </>
                          ) : salesRep ? (
                            <>
                              <Avatar
                                src={salesRep.userProfileImage?.key ? getAssetURL(salesRep.userProfileImage?.key, salesRep.userProfileImage?.url) || undefined : undefined}
                                alt={salesRepName || 'Sales Rep'}
                                sx={{ width: 28, height: 28, borderRadius: "50%" }}
                              />
                              <Typography sx={{
                                color: AppColors.blackColor, whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                                {salesRepName || 'Sales Rep'}
                              </Typography>
                            </>
                          ) : (<></>)}
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  <Box display={'flex'} justifyContent={'center'} py={1}>
                    <LoadingButton
                      sx={{
                        minWidth: '100px',
                        borderColor: AppColors.orangeBGColor,
                        backgroundColor: AppColors.orangeBGColor,
                        color: '#ffffff',
                        textTransform: 'unset',
                        fontWeight: '600',
                        '& span span': {
                          color: '#ffffff',
                        },
                      }}
                      type='submit'
                      loading={isSubmitting}
                      variant='outlined'
                    >
                      Request My Demo
                    </LoadingButton>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      // textAlign: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        width: '100%',
                        textAlign: 'start',
                        fontSize: {
                          xs: typographyClamps.caption,
                          sm: typographyClamps.body2,
                        },
                      }}
                    >
                      Your info is used only to schedule your demo.
                    </Typography>

                    <Box
                      sx={{
                        display: 'grid',
                        rowGap: 0.5,
                        mt: 1,
                      }}
                    >
                      {featureList.map((item, index) => {
                        return (
                          <Box
                            key={index}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {item.iconPath ? (
                              <Box
                                component='img'
                                src={item.iconPath}
                                height='24px'
                                width='24px'
                              />
                            ) : (
                              <ThickCheckIcon color='success' />
                            )}
                            <Typography
                              sx={{
                                fontSize: typographyClamps.body1,
                              }}
                            >
                              {item.title}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        {/* <DialogActions sx={{ paddingX: 3, justifyContent: 'center' }}>
          <Box
            component={'img'}
            src={'/static/images/knocard_auto_logo.png'}
            sx={{ height: '40px' }}
          ></Box>
        </DialogActions> */}
      </form>
    </>
  );

  return (
    <Dialog
      maxWidth={step === 'confirmation' ? 'sm' : 'lg'}
      fullWidth
      open={toggle}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      aria-describedby='alert-dialog-slide-description'
      sx={{
        '& .MuiPaper-root': {
          borderRadius: '12px', // Set your desired radius here
        },
      }}
    >
      {step === 'dealer' && renderDealerStep()}
      {step === 'confirmation' && renderConfirmation()}

      {videoUrl && (
        <VideoModel
          onClose={onCloseVideo}
          toggle={toggleVideo}
          videoUrl={videoUrl ?? ''}
          title=''
        />
      )}
    </Dialog>
  );
};

export default BookDemoFormModel;
